import { useState, type FormEvent } from 'react'
import { authApi } from '../api/auth'
import { ApiError } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import { getPasskeyAssertion, isPasskeySupported } from '../lib/webauthn'

export function LoginPage() {
  const { refresh } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [passkeyBusy, setPasskeyBusy] = useState(false)

  const passkeySupported = isPasskeySupported()

  async function onPasswordSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await authApi.login(email, password)
      await refresh()
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError('Fel e-post eller lösenord. Efter 5 misslyckade försök låses kontot i 15 minuter.')
      } else {
        setError('Något gick fel. Försök igen.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  async function onPasskeyLogin() {
    setError(null)
    setPasskeyBusy(true)
    try {
      const options = await authApi.passkeyRequestOptions()
      const credentialJson = await getPasskeyAssertion(options)
      await authApi.passkeyLogin(credentialJson)
      await refresh()
    } catch (err) {
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        setError('Passkey-inloggning avbröts.')
      } else if (err instanceof ApiError) {
        setError('Passkey-inloggningen godkändes inte. Prova lösenord istället.')
      } else {
        setError('Passkey-inloggning misslyckades. Prova lösenord istället.')
      }
    } finally {
      setPasskeyBusy(false)
    }
  }

  return (
    <main className="auth-container">
      <h1>Logga in</h1>

      {passkeySupported && (
        <>
          <button
            type="button"
            className="primary"
            onClick={onPasskeyLogin}
            disabled={passkeyBusy}
          >
            {passkeyBusy ? 'Väntar på passkey…' : 'Logga in med passkey (Touch ID)'}
          </button>
          <p className="muted small divider">eller med lösenord</p>
        </>
      )}

      <form onSubmit={onPasswordSubmit} noValidate>
        <div className="field">
          <label htmlFor="login-email">E-post</label>
          <input
            id="login-email"
            type="email"
            autoComplete="username webauthn"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="login-password">Lösenord</label>
          <input
            id="login-password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        {error && (
          <div role="alert" className="alert-error">
            {error}
          </div>
        )}

        <button type="submit" disabled={submitting}>
          {submitting ? 'Loggar in…' : 'Logga in'}
        </button>
      </form>
    </main>
  )
}
